import React, { useState, useEffect } from 'react';
import { ArrowLeft, Loader } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getScoringComponent } from '../../scoring/scoringComponents';
import { apiCall } from '../../services/apiUtil';

/**
 * GameDetailTab — Dynamic tab for viewing a specific past game.
 *
 * Props:
 * - voProvider: SquadDetailsProvider instance
 * - gameCommonId: game_common.id for the game to show
 * - eventId: the event the game belongs to
 * - onClose: called when the user closes the tab
 */
const GameDetailTab = ({ voProvider, gameCommonId, eventId, onClose }) => {
  const { theme } = useTheme();

  const group = voProvider?.getGroup();
  const groupId = voProvider?.vo?.groupId;
  const members = voProvider?.getMembers();
  const sportTypeId = group?.sportTypeId || group?.sportType?.id;

  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  const [ScoringComponent, setScoringComponent] = useState(undefined); // undefined = resolving

  // Resolve sport-specific scoring component
  useEffect(() => {
    if (!sportTypeId) { setScoringComponent(null); return; }
    setScoringComponent(undefined);
    getScoringComponent(sportTypeId, 'pastGames').then(component => {
      setScoringComponent(component ? () => component : null);
    });
  }, [sportTypeId]);

  // Load event data by eventId
  useEffect(() => {
    if (!eventId) { setLoadingEvent(false); return; }
    setLoadingEvent(true);
    apiCall(`/events/${eventId}`)
      .then(data => setEvent(data))
      .catch(err => console.error('[GameDetailTab] Failed to load event:', err))
      .finally(() => setLoadingEvent(false));
  }, [eventId]);

  if (loadingEvent || ScoringComponent === undefined) {
    return (
      <div className="flex justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={onClose}
        className={`flex items-center gap-x-2 mb-4 px-4 py-2 rounded-lg ${theme.backgroundSecondary} ${theme.text} hover:opacity-80 transition-opacity`}
      >
        <ArrowLeft className="w-4 h-4" />
        <span>Back to Past Games</span>
      </button>

      {!ScoringComponent ? (
        <div className={`${theme.card} rounded-2xl border ${theme.border} p-6`}>
          <p className={`text-center text-lg ${theme.text}`}>
            Scoring card not available for this sport
          </p>
        </div>
      ) : (
        <ScoringComponent
          gameCommonId={gameCommonId}
          event={event}
          groupId={groupId}
          members={members}
          onBack={onClose}
          onClose={onClose}
        />
      )}
    </div>
  );
};

export default GameDetailTab;
